import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import {
  apiClient,
  getAccessToken,
  setAccessToken,
  subscribeAccessToken,
  setOnAuthLost,
  refreshAccessToken,
  accessTokenExpiresAt,
} from '../api/client';

const AuthContext = createContext();

const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  loading: true,
  error: null
};

const authReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload, error: null };

    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };

    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        loading: false,
        error: null
      };

    case 'TOKEN_REFRESHED':
      return {
        ...state,
        token: action.payload,
      };

    case 'LOGOUT':
      return {
        ...initialState,
        loading: false
      };

    case 'CLEAR_ERROR':
      return { ...state, error: null };

    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [hasCheckedAuth, setHasCheckedAuth] = React.useState(false);

  useEffect(() => {
    return subscribeAccessToken((token) => {
      if (token) {
        dispatch({ type: 'TOKEN_REFRESHED', payload: token });
      }
    });
  }, []);

  useEffect(() => {
    setOnAuthLost(() => {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem('user');
      }
      dispatch({ type: 'LOGOUT' });
    });
    return () => setOnAuthLost(() => {});
  }, []);

  useEffect(() => {
    if (hasCheckedAuth) return;

    const checkAuth = async () => {
      setHasCheckedAuth(true);
      const token = getAccessToken();
      try {
        if (!token) {
          await refreshAccessToken();
        }
        const response = await apiClient.get('/auth/me');
        const current = getAccessToken();
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem('user', JSON.stringify(response.data));
        }
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: { user: response.data, token: current }
        });
      } catch (error) {
        setAccessToken(null);
        if (typeof localStorage !== 'undefined') {
          localStorage.removeItem('user');
        }
        dispatch({ type: 'LOGOUT' });
      }
    };

    checkAuth();
  }, [hasCheckedAuth]);

  useEffect(() => {
    if (!state.token) return undefined;

    const refreshIfDue = () => {
      const expMs = accessTokenExpiresAt(getAccessToken());
      if (!expMs || expMs - Date.now() > 120_000) return;
      refreshAccessToken().catch(() => {});
    };

    refreshIfDue();
    const id = setInterval(refreshIfDue, 60_000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshIfDue();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', refreshIfDue);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', refreshIfDue);
    };
  }, [state.token]);

  const login = useCallback(async (username, password) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      const response = await apiClient.post('/auth/login', {
        username,
        password
      });

      const { access_token, user } = response.data;
      setAccessToken(access_token);
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('user', JSON.stringify(user));
      }

      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user, token: access_token }
      });

      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Login failed';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      return { success: false, error: errorMessage };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiClient.post('/auth/logout');
    } catch (error) {
      // Logout API error - proceed with local cleanup
    }

    setAccessToken(null);
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('user');
    }
    dispatch({ type: 'LOGOUT' });
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  const value = {
    user: state.user,
    token: state.token,
    isAuthenticated: state.isAuthenticated,
    loading: state.loading,
    error: state.error,
    login,
    logout,
    clearError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
